import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronRight,
  Gavel,
  Loader2,
  RefreshCcw,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import WizardStepper from "@/components/wizard/WizardStepper";
import TutorialDeepLink from "@/components/TutorialDeepLink";
import LegalWarningBanner from "@/components/legal/LegalWarningBanner";
import LegalCorpusManager from "@/components/legal/LegalCorpusManager";
import LegalAnalysisView from "@/components/legal/LegalAnalysisView";
import { getProject } from "@/lib/profiles";
import { getLatestAnalysis } from "@/lib/analyses";
import { listLegalTexts, runLegalAnalysis } from "@/lib/legal";

export default function WizardLegal() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [legalTexts, setLegalTexts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, an, texts] = await Promise.all([
        getProject(projectId),
        getLatestAnalysis(projectId),
        listLegalTexts(),
      ]);
      if (!proj) {
        toast.error("Projet introuvable.");
        navigate("/app/dashboard", { replace: true });
        return;
      }
      setProject(proj);
      setAnalysis(an);
      setLegalTexts(texts || []);
      // Preselect texts if the analysis was already run
      const existingIds =
        an?.results?.legal_analysis?.source_legal_text_ids || [];
      if (existingIds.length) {
        setSelectedIds(existingIds);
      }
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible de charger la page juridique."
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const legalResult = analysis?.results?.legal_analysis || null;
  const currency = analysis?.results?.summary?.currency || "UM";

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleUploaded = (t) => {
    setLegalTexts((prev) => [t, ...prev]);
    setSelectedIds((prev) => [...prev, t.id]);
  };

  const handleDeleted = (id) => {
    setLegalTexts((prev) => prev.filter((x) => x.id !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const canRun = useMemo(
    () => !!analysis && selectedIds.length >= 1 && !running,
    [analysis, selectedIds, running]
  );

  const runAnalysis = async (force = false) => {
    if (!analysis) {
      toast.error(
        "Aucune analyse vitaliste disponible. Lancez d'abord l'étape 3."
      );
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Sélectionnez au moins un texte juridique.");
      return;
    }
    setRunning(true);
    try {
      const res = await runLegalAnalysis(analysis.id, {
        legalTextIds: selectedIds,
        force,
      });
      // Refresh the analysis so the UI reflects the persisted legal_analysis
      const fresh = await getLatestAnalysis(projectId);
      setAnalysis(fresh);
      toast.success(
        res.cached
          ? "Analyse juridique chargée depuis le cache."
          : "Analyse juridique générée avec succès."
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "L'analyse juridique a échoué."
      );
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center"
        data-testid="wizard-legal-loading"
      >
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wizard-legal-page">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Nouveau projet · Étape 5/7
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Analyse juridique vitaliste
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {project?.name}
          {project?.fiscal_year && ` · Exercice ${project.fiscal_year}`}
          {" "}— Qualification potentielle des infractions sur la base de votre
          corpus juridique et conversion des préjudices en jours de vie humaine.
        </p>
      </div>

      <WizardStepper current={5} />
      <TutorialDeepLink
        chapter={6}
        section="section-6-1"
        id="wizard-step-5-legal"
        label="Préparer la bibliothèque juridique pour cette analyse"
        duration={10}
      />

      {/* Mandatory non-dismissible pedagogical banner */}
      <LegalWarningBanner />

      {/* Analysis prerequisite guard */}
      {!analysis && (
        <Card className="border-dashed" data-testid="no-vitalist-card">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Info className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">
              Analyse vitaliste requise
            </h3>
            <p className="max-w-md text-sm text-muted-foreground">
              Lancez d'abord l'analyse vitaliste (étape 3) pour obtenir les
              données budgétaires nécessaires au module juridique.
            </p>
            <Button
              onClick={() =>
                navigate(`/app/projects/${projectId}/wizard/confirm`)
              }
              data-testid="go-to-confirm-btn"
            >
              Aller à l'étape 3
            </Button>
          </CardContent>
        </Card>
      )}

      {analysis && (
        <>
          {/* Corpus manager */}
          <LegalCorpusManager
            texts={legalTexts}
            selectedIds={selectedIds}
            onToggle={toggleSelect}
            onUploaded={handleUploaded}
            onDeleted={handleDeleted}
          />

          {/* Run action */}
          <Card className="border-brand-gold/30 bg-brand-gold/5">
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <Gavel className="mt-0.5 h-5 w-5 text-brand-blue dark:text-brand-gold" />
                <div className="text-sm">
                  <div className="font-semibold">
                    {selectedIds.length} texte
                    {selectedIds.length > 1 ? "s" : ""} sélectionné
                    {selectedIds.length > 1 ? "s" : ""}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {legalResult
                      ? "Une analyse existe déjà — relancez pour régénérer."
                      : "1 seul appel GPT-4o sera effectué, mis en cache côté serveur."}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {legalResult && (
                  <Button
                    variant="outline"
                    onClick={() => runAnalysis(true)}
                    disabled={!canRun}
                    data-testid="rerun-legal-btn"
                  >
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Régénérer
                  </Button>
                )}
                <Button
                  onClick={() => runAnalysis(false)}
                  disabled={!canRun}
                  data-testid="run-legal-btn"
                >
                  {running ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyse en cours…
                    </>
                  ) : (
                    <>
                      <Gavel className="mr-2 h-4 w-4" />
                      {legalResult
                        ? "Relancer l'analyse juridique"
                        : "Lancer l'analyse juridique"}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {legalResult ? (
            <LegalAnalysisView legal={legalResult} currency={currency} />
          ) : (
            <Card
              className="border-dashed"
              data-testid="legal-result-empty"
            >
              <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
                <Gavel className="h-10 w-10 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Aucune analyse générée</h3>
                <p className="max-w-md text-sm text-muted-foreground">
                  Sélectionnez vos textes de référence et cliquez sur
                  « Lancer l'analyse juridique ».
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Footer nav */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/results`)
          }
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Étape précédente
        </Button>
        <Button
          variant="default"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/judgment`)
          }
          disabled={!analysis?.results?.legal_analysis}
          data-testid="wizard-next-judgment-btn"
        >
          Simuler le jugement
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
